import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  CalendarDays,
  Receipt,
  Tags,
  BarChart3,
  Sparkles,
  Shield,
  Landmark,
  Scale,
  X,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  Grid3X3,
  Settings,
  Ship,
  BookOpen,
  Group,
  ArrowLeftRight,
} from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useUIStore } from '@/shared/stores/uiStore';
import { cn } from '@/shared/lib/utils';
import { IconButton } from '@/shared/ui/icon-button/IconButton';

const navigation = [
  { name: 'Periodos', path: '/periodos', icon: CalendarDays },
  { name: 'Comparativo Mensual', path: '/comparativo-mensual', icon: LayoutDashboard, requireViewFinancials: true },
  { name: 'Comparativo Anual', path: '/comparativo-anual', icon: BarChart3, requireViewFinancials: true },
  { name: 'Centros de Costo', path: '/centros-costo', icon: Landmark },
  { name: 'Gasto Clasificado', path: '/gasto-clasificado', icon: Grid3X3 },
  { name: 'Balance General', path: '/balance-general', icon: Scale, requireViewFinancials: true },
  { name: 'Deducciones', path: '/deducciones', icon: Receipt, requireManage: true },
  { name: 'Clasificaciones', path: '/clasificaciones', icon: Tags, requireManage: true },
  { name: 'Agrupaciones Análisis', path: '/agrupaciones-analisis', icon: Group, requireManage: true },
  { name: 'Config. Estado Resultados', path: '/config-estado-resultado', icon: Settings, requireManage: true },
  { name: 'Costos Subaplicados', path: '/costos-subaplicados', icon: Ship },
  { name: 'Mayor por Cuenta', path: '/mayor-cuentas', icon: BookOpen },
  { name: 'Análisis', path: '/analisis', icon: Sparkles, requireViewFinancials: true },
  { name: 'Auditoría', path: '/auditoria', icon: Shield, requireManage: true },
  { name: 'Cuentas Intercompany', path: '/intercompany-cuentas', icon: ArrowLeftRight, requireManage: true },
];

export function Sidebar() {
  const canManage = useAuthStore(state => state.canManage)();
  const canViewFinancials = useAuthStore(state => state.canViewFinancials)();
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);

  const isMobile = useUIStore(state => state.isMobile);
  const isSidebarOpen = useUIStore(state => state.isSidebarOpen);
  const setSidebarOpen = useUIStore(state => state.setSidebarOpen);
  const isSidebarCollapsed = useUIStore(state => state.isSidebarCollapsed);
  const toggleSidebarCollapsed = useUIStore(state => state.toggleSidebarCollapsed);

  const location = useLocation();
  const visibleNav = navigation.filter(
    item =>
      (!item.requireManage || canManage) &&
      (!item.requireViewFinancials || canViewFinancials)
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-30 hidden h-screen flex-col border-r border-border bg-surface shadow-elevated transition-[width] duration-300 lg:flex',
          isSidebarCollapsed ? 'w-20' : 'w-72'
        )}
      >
        <div
          className={cn(
            'flex items-center border-b border-border',
            isSidebarCollapsed ? 'flex-col gap-2 py-3' : 'h-20 justify-between px-6'
          )}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-lg shadow-emerald-500/20">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div>
                <p className="font-display text-lg font-bold leading-tight text-text-primary">
                  AURORA
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                  Estados Financieros
                </p>
              </div>
            )}
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            title={isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            onClick={toggleSidebarCollapsed}
          >
            {isSidebarCollapsed ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </IconButton>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-6">
          {!isSidebarCollapsed && (
            <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-wider text-text-muted">
              Módulos
            </p>
          )}
          <ul className="space-y-1.5">
            {visibleNav.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    title={isSidebarCollapsed ? item.name : undefined}
                    className={cn(
                      'group flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200',
                      isActive
                        ? 'bg-primary text-text-inverse shadow-glow-primary'
                        : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary',
                      isSidebarCollapsed && 'justify-center px-0'
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className={cn('h-5 w-5', isActive && 'stroke-[2.5px]')} />
                      {!isSidebarCollapsed && item.name}
                    </span>
                    {isActive && !isSidebarCollapsed && <ChevronRight className="h-4 w-4" />}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-border p-4">
          {!isSidebarCollapsed && (
            <div className="mb-3 rounded-xl bg-surface-elevated p-3">
              <p className="text-xs font-semibold text-text-primary">{user?.nombre || 'Usuario'}</p>
              <p className="text-[10px] text-text-muted">{user?.email || user?.cedula}</p>
            </div>
          )}
          <button
            onClick={logout}
            title={isSidebarCollapsed ? 'Cerrar sesión' : undefined}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-text-secondary transition-colors hover:bg-danger-bg hover:text-danger',
              isSidebarCollapsed && 'justify-center px-0'
            )}
          >
            <LogOut className="h-5 w-5" />
            {!isSidebarCollapsed && 'Cerrar sesión'}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar as full-screen sheet */}
      <AnimatePresence>
        {isMobile && isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 z-40 bg-primary/40 backdrop-blur-sm lg:hidden"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
              className="fixed left-0 top-0 z-50 h-screen w-[85vw] max-w-sm bg-surface shadow-2xl lg:hidden"
            >
              <div className="flex h-20 items-center justify-between border-b border-border px-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-lg shadow-emerald-500/20">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold leading-tight text-text-primary">
                      AURORA
                    </p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                      Estados Financieros
                    </p>
                  </div>
                </div>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Cerrar menú"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-5 w-5" />
                </IconButton>
              </div>

              <nav className="flex-1 overflow-y-auto px-4 py-6">
                <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                  Módulos
                </p>
                <ul className="space-y-2">
                  {visibleNav.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={cn(
                            'group flex items-center justify-between rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200',
                            isActive
                              ? 'bg-primary text-text-inverse shadow-glow-primary'
                              : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary'
                          )}
                        >
                          <span className="flex items-center gap-3">
                            <Icon className={cn('h-5 w-5', isActive && 'stroke-[2.5px]')} />
                            {item.name}
                          </span>
                          {isActive && <ChevronRight className="h-5 w-5" />}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="absolute bottom-0 left-0 right-0 border-t border-border p-4">
                <div className="mb-3 rounded-xl bg-surface-elevated p-3">
                  <p className="text-sm font-semibold text-text-primary">{user?.nombre || 'Usuario'}</p>
                  <p className="text-xs text-text-muted">{user?.email || user?.cedula}</p>
                </div>
                <button
                  onClick={logout}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-text-secondary transition-colors hover:bg-danger-bg hover:text-danger"
                >
                  <LogOut className="h-5 w-5" />
                  Cerrar sesión
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
